import { countRemarkIssues } from '../RemarkPopover';
import {
  amendmentNumberIn,
  latestAmendmentIn,
  namesAmendment,
  namesMaster,
  namesNumberedSchedule,
  namesOrderSchedule,
  namesRestatement,
  scheduleNumberIn,
} from './instrumentName';

/**
 * How the explorer's ledger reads a tree row: what kind of instrument it is,
 * the short id the Consolidation Ledger gives it (MASTER, OS2, A4), and the
 * figures its columns show. Everything here comes off the folder listing the
 * tree is built from; a column it has nothing for shows a dash instead.
 */

export type InstrumentKind =
  | 'master'
  | 'schedule'
  | 'amendment'
  | 'missing'
  /** A generated document: a summary (S) or a consolidated version (C). */
  | 'document'
  /** A document that is none of the above. */
  | 'file'
  | 'folder';

export interface Instrument {
  kind: InstrumentKind;
  /** The letters in the row's glyph; empty for a file or a folder, which draw an icon. */
  glyph: string;
  /** MASTER, OS2, A4 — when the name says which; null otherwise. */
  docId: string | null;
  /**
   * A document or contract, as opposed to a folder that only groups the tree
   * (a section, a party, Originals). Only instruments get the ledger's dashes.
   */
  isInstrument: boolean;
}

/**
 * A remark as the folder listing sends it. Its level is wider than the
 * remarks panel's own type: the server also sends `info`.
 */
/**
 * The Consolidation Ledger's severity for a set of remarks: the worst of them.
 * The server also sends `info` — a reference it could not resolve yet, say —
 * which the ledger draws quieter than a warning.
 */
export type AlertSeverity = 'crit' | 'high' | 'info';

export const alertSeverity = (remark: { level: string }[]): AlertSeverity => {
  const levels = remark.map((r) => r.level);
  if (levels.includes('error')) return 'crit';
  if (levels.includes('warning')) return 'high';
  return 'info';
};

export interface LedgerRemark {
  level: string;
  message: string;
  details?: unknown[];
}

/** The parts of a tree item the ledger reads. */
export interface LedgerItem {
  fileType?: string;
  label?: unknown;
  fileData?: {
    /** The file's own name; the row's label may be its title instead. */
    filename?: string;
    file_type?: string | null;
    contract_type?: string | null;
    /** The title the server read off the document itself. */
    contract_title?: string | null;
    /** When the document takes effect, where the host has read it. */
    effective_date?: string | null;
    contract_date?: string | null;
    amendment_date?: string | null;
    signature_info?: { party_name?: string | null }[];
    remark?: LedgerRemark[];
  };
  folderData?: {
    folder_type?: string;
    /** The contract's own title, which the server sends without its parties. */
    contract_title?: string | null;
    signature_info?: { party_name?: string | null }[];
    is_bucket?: boolean;
    is_missing?: boolean;
    contract_type?: string;
    remarks?: LedgerRemark[];
  };
  missing?: {
    kind?: string;
    amendmentNumber?: number | null;
    amendmentNumbers?: number[];
  };
  /**
   * The parties of the contract the row is filed under, which the host passes
   * down for a row that names none itself.
   */
  contractParties?: string[];
  children?: unknown[];
}

const FOLDER: Instrument = {
  kind: 'folder',
  glyph: '',
  docId: null,
  isInstrument: false,
};

const MASTER_AGREEMENT: Instrument = {
  kind: 'master',
  glyph: 'M',
  docId: 'MASTER',
  isInstrument: true,
};

const PLAIN_FILE: Instrument = {
  kind: 'file',
  glyph: '',
  docId: null,
  isInstrument: true,
};

/** Folders that stand for a contract rather than just grouping the tree. */
const CONTRACT_FOLDERS = ['contract', 'title'];

/** Generated documents, read by their type rather than their name. */
const DERIVED_GLYPH: Record<string, string> = {
  summary: 'S',
  consolidated: 'C',
};

const childrenOf = (item: LedgerItem): LedgerItem[] =>
  (item.children as LedgerItem[] | undefined) ?? [];

const plainDocument = (glyph: string): Instrument => ({
  kind: 'document',
  glyph,
  docId: null,
  isInstrument: true,
});

/** An amendment, numbered by its name where the name gives a number. */
const amendmentNamed = (name: string): Instrument => {
  const number = amendmentNumberIn(name);
  return {
    kind: 'amendment',
    glyph: 'A',
    docId: number !== null ? `A${number}` : null,
    isInstrument: true,
  };
};

/** An order schedule, numbered by its name where the name gives a number. */
const scheduleNamed = (name: string): Instrument => {
  const number = scheduleNumberIn(name);
  return {
    kind: 'schedule',
    glyph: 'OS',
    docId: number !== null ? `OS${number}` : null,
    isInstrument: true,
  };
};

/**
 * The master or the order schedule a name calls itself, if either. A
 * schedule's title often names the master it is made under ("Order Schedule
 * No. 5 to the Master Agreement"), so a numbered schedule is looked for first.
 */
const masterOrScheduleNamed = (name: string): Instrument | null => {
  if (namesNumberedSchedule(name)) return scheduleNamed(name);
  if (namesMaster(name)) return MASTER_AGREEMENT;
  if (namesOrderSchedule(name)) return scheduleNamed(name);
  return null;
};

/**
 * The instrument a name calls itself, if it calls itself one. A restatement
 * is the instrument it restates, again in full — an amended and restated order
 * schedule is that schedule — so it is read before the word "amendment".
 */
const instrumentNamed = (name: string): Instrument | null => {
  if (namesRestatement(name)) return masterOrScheduleNamed(name);
  if (namesAmendment(name)) return amendmentNamed(name);
  return masterOrScheduleNamed(name);
};

/**
 * A row read by its title, then by its name: the title says which instrument
 * it is, and the name stands in where the title says nothing, or numbers it
 * where the title gives the same kind without a number.
 */
const byTitleThenName = (
  read: (name: string) => Instrument | null,
  title: string,
  name: string
): Instrument | null => {
  const byTitle = read(title);
  const byName = read(name);
  if (!byTitle) return byName;
  if (byTitle.docId || byName?.kind !== byTitle.kind) return byTitle;
  return byName;
};

/**
 * A row the server types as a standard contract, which is never an amendment:
 * the master or an order schedule, as its title or name says, or else a plain
 * file.
 */
const describeStandard = (title: string, name: string): Instrument =>
  byTitleThenName(masterOrScheduleNamed, title, name) ?? PLAIN_FILE;

/**
 * A row the server types as an amendment. The server types an amended and
 * restated schedule the same way, so where the name says it is a master or a
 * schedule — restated, or with no word of an amendment in it ("S and P -
 * OS4.pdf") — it reads as that; otherwise it is an amendment.
 */
const describeAmendment = (title: string, name: string): Instrument =>
  byTitleThenName(instrumentNamed, title, name) ?? amendmentNamed(name);

/** A contract row's own title, without its parties; a file's own name. */
const nameOf = (item: LedgerItem): string => {
  const label = String(item.label ?? '');
  if (item.fileType === 'folder')
    return item.folderData?.contract_title || label;
  return item.fileData?.filename ?? label;
};

/**
 * What kind of instrument a row is. One rule for every row — a contract or a
 * file, in any section of either view:
 *
 * 1. A generated document is one: a summary (S) or a consolidated version
 *    (C), whatever amendment it is named after.
 * 2. The server's type says what family the row is in — a standard contract
 *    or an amendment — and the title the server read off a file, then the
 *    row's name, says which instrument in it, and its number (OS2, A9).
 * 3. A row the server has not typed yet goes by its title and name alone.
 */
export const describeInstrument = (
  item: LedgerItem | undefined
): Instrument => {
  if (!item) return FOLDER;
  const name = String(item.label ?? '');

  if (item.missing) {
    const numbers = item.missing.amendmentNumbers ?? [];
    const number = item.missing.amendmentNumber ?? amendmentNumberIn(name);
    // A gap covering a run is labelled by its ends: A1–21.
    const docId =
      numbers.length > 1
        ? `A${Math.min(...numbers)}–${Math.max(...numbers)}`
        : number != null
        ? `A${number}`
        : null;
    return {
      kind: 'missing',
      glyph: '?',
      docId,
      isInstrument: true,
    };
  }

  const folder = item.folderData;
  if (item.fileType === 'folder') {
    const isContract =
      !!folder &&
      !folder.is_bucket &&
      CONTRACT_FOLDERS.includes(folder.folder_type ?? '');
    if (!isContract) return FOLDER;
  }

  const file = item.fileData;
  const derived = DERIVED_GLYPH[file?.file_type ?? ''];
  if (derived) return plainDocument(derived);

  const contractType = file?.contract_type ?? folder?.contract_type;
  if (contractType === 'consolidated') return plainDocument('C');

  const rowName = nameOf(item);
  const title = file?.contract_title ?? '';
  if (contractType === 'standard') return describeStandard(title, rowName);
  if (contractType === 'amendment') return describeAmendment(title, rowName);
  return byTitleThenName(instrumentNamed, title, rowName) ?? PLAIN_FILE;
};

/** The kinds of instrument in a chain, in the order the chain runs. */
const CHAIN_ORDER = ['MASTER', 'OS', 'A'];

/**
 * An id's place in the chain, as [its kind's place, its number]: A10 → [2, 10].
 * A run of skipped amendments (A1–21) takes the place of its first number.
 */
const chainPosition = (docId: string): [number, number] => {
  const [, kind = '', number = ''] = /^([A-Z]+)(\d*)/.exec(docId) ?? [];
  return [CHAIN_ORDER.indexOf(kind), Number(number)];
};

/**
 * Orders two instrument ids (MASTER, OS2, A9) as the chain runs: the master,
 * then the order schedules, then the amendments, each kind by number — so A9
 * comes before A10, where comparing the text would put A10 first.
 */
export const compareDocIds = (a: string, b: string): number => {
  const [kindA, numberA] = chainPosition(a);
  const [kindB, numberB] = chainPosition(b);
  return kindA - kindB || numberA - numberB;
};

/**
 * The file a contract folder stands for: its own file, sitting directly under
 * it (the hierarchy view) or in its Originals folder (the folder view), and
 * failing both, the generated document it holds (a consolidated version).
 */
const instrumentFile = (item: LedgerItem): LedgerItem['fileData'] => {
  if (item.fileData) return item.fileData;
  const isOwnFile = (child: LedgerItem) =>
    !!child.fileData && !DERIVED_GLYPH[child.fileData.file_type ?? ''];

  const children = childrenOf(item);
  const own = children.find(isOwnFile);
  if (own) return own.fileData;
  const originals = children.find(
    (child) => child.folderData?.folder_type === 'original'
  );
  const original = originals
    ? childrenOf(originals).find(isOwnFile)?.fileData
    : undefined;
  // A consolidated version holds only the document generated for it, which
  // is its own file.
  return original ?? children.find((child) => !!child.fileData)?.fileData;
};

/**
 * The last amendment a consolidated version takes in, as its name says — the
 * row's own, or the generated document's beneath a contract row ("Consolidated
 * Order Schedule Number 2 (with amendments 1, 2, and 3).pdf" → 3). Null for any
 * other row, and for a version whose name lists no amendments.
 */
export const consolidatedThrough = (item: LedgerItem): number | null => {
  if (describeInstrument(item).glyph !== DERIVED_GLYPH.consolidated)
    return null;
  const names = [
    nameOf(item),
    String(item.label ?? ''),
    instrumentFile(item)?.filename,
  ];
  for (const name of names) {
    const number = name ? latestAmendmentIn(name) : null;
    if (number !== null) return number;
  }
  return null;
};

/** The dates a document can be placed by, under the API's names. */
export interface DocumentDates {
  effective_date?: string | null;
  contract_date?: string | null;
  amendment_date?: string | null;
}

/**
 * The date a document takes effect: the one rule every screen that shows it
 * uses (the explorer's Effective column and register, the detail panel, the
 * edit form). `contract_date` is the commencement date where there is one, so
 * the effective date comes first; without it, that date, or an amendment's own.
 */
export const documentEffectiveDate = (
  dates: DocumentDates | null | undefined
): string | null =>
  dates?.effective_date ||
  dates?.contract_date ||
  dates?.amendment_date ||
  null;

/** The date an instrument takes effect: its file's (`documentEffectiveDate`). */
export const effectiveDateOf = (item: LedgerItem): string | null =>
  documentEffectiveDate(instrumentFile(item));

/**
 * The parties a set of signature blocks names, each once, in the order they
 * sign: a party with several signatories signs more than one block.
 */
export const partyNamesIn = (
  signatures: { party_name?: string | null }[] | undefined
): string[] => {
  const names = (signatures ?? [])
    .map((party) => party.party_name?.trim())
    .filter((name): name is string => !!name);
  return names.filter(
    (name, index) =>
      names.findIndex((other) => other.toLowerCase() === name.toLowerCase()) ===
      index
  );
};

/**
 * The parties to a row's document, from its signature blocks — a contract
 * folder's and a file's alike. A row with none — a consolidated version, or
 * its generated document, which has no signature page — is between the
 * parties of the contract it is filed under.
 */
export const partiesOf = (item: LedgerItem): string[] => {
  const parties = partyNamesIn(
    item.fileType === 'folder'
      ? item.folderData?.signature_info
      : item.fileData?.signature_info
  );
  return parties.length ? parties : item.contractParties ?? [];
};

export const remarksOf = (item: LedgerItem): LedgerRemark[] =>
  item.fileData?.remark ?? item.folderData?.remarks ?? [];

export interface RemarkTally {
  count: number;
  /** The worst of them, which colours the badge. */
  severity: AlertSeverity;
}

/** The issues on the rows under this one, for the badge a collapsed row shows. */
export const remarksBelow = (item: LedgerItem): RemarkTally => {
  let count = 0;
  const all: LedgerRemark[] = [];
  const walk = (nodes: LedgerItem[]) => {
    nodes.forEach((node) => {
      const remark = remarksOf(node);
      if (remark.length) {
        count += countRemarkIssues(remark);
        all.push(...remark);
      }
      walk(childrenOf(node));
    });
  };
  walk(childrenOf(item));
  return { count, severity: alertSeverity(all) };
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const NUMERIC_DATE = /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$/;

/**
 * "08 May 2011", as the ledger writes its dates; the value verbatim when it is
 * not a date. A bare date is a calendar day, so it is read as written rather
 * than as an instant, which west of Greenwich would come out a day early.
 * An all-numeric date ("05/01/2024", as a user's date format may render one)
 * is also kept as written: it could be day-first or month-first.
 */
export const formatLedgerDate = (value: string): string => {
  const day = DATE_ONLY.exec(value);
  if (day) return `${day[3]} ${MONTHS[Number(day[2]) - 1]} ${day[1]}`;
  if (NUMERIC_DATE.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const dd = String(date.getDate()).padStart(2, '0');
  return `${dd} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};
